import type {
  CheckoutThemeConversionQuery,
  CheckoutThemeConversionResponse,
  CheckoutThemeProgramRequest,
  CheckoutThemeProgramResponse,
  LinkedRoutingConfigRetrieveResponse,
  MerchantRoutingAlgorithm,
  ProfileDefaultRoutingConfig,
  ProfileDeniedConnectorsResponse,
  RoutableConnectorChoice,
  RoutingActivatePayload,
  RoutingConfigCreateRequest,
  RoutingConfigDeleteResponse,
  RoutingConfigHistoryResponse,
  RoutingConfigUpdateRequest,
  RoutingConnectorCaps,
  RoutingDeactivateRequest,
  RoutingDictionary,
  RoutingDictionaryRecord,
  RoutingHistoryParams,
  RoutingVolumeCounters,
  SurchargeRuleRequest,
  SurchargeRuleResponse,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Create and manage payment routing algorithms.
 *
 * Routing rules determine which gateway connector handles each payment based on
 * card type, currency, amount, or custom conditions.
 */
export class Routing {
  readonly decision: RoutingDecisionManager;
  /** Merchant-friendly, per-payment-method surcharge rules (no Euclid DSL). */
  readonly surchargeRules: SurchargeRules;
  /** Which stored appearance variant a buyer is shown. Decides a look, never a payment. */
  readonly checkoutThemeRules: CheckoutThemeRules;
  /** Rendered-to-paid conversion per appearance variant and segment. */
  readonly checkoutThemeConversion: CheckoutThemeConversion;

  constructor(private readonly request: RequestFn) {
    this.decision = new RoutingDecisionManager(request);
    this.surchargeRules = new SurchargeRules(request);
    this.checkoutThemeRules = new CheckoutThemeRules(request);
    this.checkoutThemeConversion = new CheckoutThemeConversion(request);
  }

  /**
   * Create a new routing algorithm.
   *
   * @param params - Routing algorithm definition (rule-based, priority, or volume-based).
   * @returns Metadata record for the created routing configuration. The full
   *   algorithm body is not echoed back — use `retrieve(id)` if you need it.
   *
   * @example
   * ```typescript
   * const config = await delopay.routing.create({
   *   name: 'EU Priority',
   *   algorithm: { type: 'priority', data: [{ connector: 'stripe' }] },
   * });
   * ```
   *
   * @example Conditional volume split (advanced): cards → 90% epayouts / 10% stripe.
   * Rules run top-down (first match wins); `defaultSelection` is the fallback.
   * Splits must sum to 100; `amount` conditions are in minor units.
   * ```typescript
   * const config = await delopay.routing.create({
   *   name: 'Card split 90/10',
   *   profile_id: 'pro_...',
   *   algorithm: {
   *     type: 'advanced',
   *     data: {
   *       defaultSelection: { type: 'priority', data: [{ connector: 'stripe' }] },
   *       rules: [
   *         {
   *           name: 'cards',
   *           connectorSelection: {
   *             type: 'volume_split',
   *             data: [
   *               { connector: { connector: 'epayouts' }, split: 90 },
   *               { connector: { connector: 'stripe' }, split: 10 },
   *             ],
   *           },
   *           statements: [
   *             {
   *               condition: [
   *                 {
   *                   lhs: 'payment_method',
   *                   comparison: 'equal',
   *                   value: { type: 'enum_variant', value: 'card' },
   *                   metadata: {},
   *                 },
   *               ],
   *             },
   *           ],
   *         },
   *       ],
   *       metadata: {},
   *     },
   *   },
   * });
   * await delopay.routing.activate(config.id);
   * ```
   */
  async create(params: RoutingConfigCreateRequest): Promise<RoutingDictionaryRecord> {
    return this.request('POST', '/routing', { body: params });
  }

  /**
   * Retrieve a routing algorithm by its ID.
   *
   * @param algorithmId - The routing algorithm ID.
   * @returns The full routing configuration including the algorithm body.
   */
  async retrieve(algorithmId: string): Promise<MerchantRoutingAlgorithm> {
    return this.request('GET', `/routing/${encodeURIComponent(algorithmId)}`);
  }

  /**
   * Activate a routing algorithm, making it the active routing strategy.
   *
   * Always sends a JSON body (default `{}`) so the request carries the
   * `Content-Type: application/json` header that the server requires.
   *
   * @param algorithmId - The routing algorithm ID to activate.
   * @param params - Optional activation payload (e.g. `transaction_type`).
   */
  async activate(
    algorithmId: string,
    params: RoutingActivatePayload = {},
  ): Promise<RoutingDictionaryRecord> {
    return this.request('POST', `/routing/${encodeURIComponent(algorithmId)}/activate`, {
      body: params,
    });
  }

  /**
   * Deactivate the currently active routing algorithm (falls back to default routing).
   *
   * Always sends a JSON body (default `{}`) so the request carries the
   * `Content-Type: application/json` header that the server requires.
   *
   * @param params - Optional deactivation payload.
   */
  async deactivate(params: RoutingDeactivateRequest = {}): Promise<RoutingDictionaryRecord> {
    return this.request('POST', '/routing/deactivate', { body: params });
  }

  /**
   * Edit a static routing configuration.
   *
   * Partial: send only the fields to change. `name`/`description` are
   * metadata-only; `algorithm` is a wholesale rule replacement, validated
   * against the shop exactly as at create. `modified_at` is bumped either way.
   *
   * `PUT /routing/{algorithmId}`
   *
   * @param algorithmId - The routing algorithm ID to edit.
   * @param params - The fields to change (at least one required).
   * @returns The updated routing configuration including the algorithm body.
   */
  async update(
    algorithmId: string,
    params: RoutingConfigUpdateRequest,
  ): Promise<MerchantRoutingAlgorithm> {
    return this.request('PUT', `/routing/${encodeURIComponent(algorithmId)}`, { body: params });
  }

  /**
   * Delete a static routing configuration.
   *
   * A soft delete: the configuration disappears from every list and read, but
   * its version history is kept server-side for audit. The live configuration
   * of a shop cannot be deleted — the backend refuses it (400, `IR_16`); deactivate it first.
   * Only `single`, `priority`, `volume_split` and `advanced` payment
   * configurations qualify (400 otherwise).
   *
   * `DELETE /routing/{algorithmId}`
   *
   * @param algorithmId - The routing algorithm to delete.
   */
  async delete(algorithmId: string): Promise<RoutingConfigDeleteResponse> {
    return this.request('DELETE', `/routing/${encodeURIComponent(algorithmId)}`);
  }

  /**
   * Every content window a routing configuration has had, oldest first.
   *
   * A configuration's rule can be edited in place, so this is what makes "which
   * rule decided this payment" answerable after the fact. Each entry is the rule
   * as it stood between `valid_from` and `valid_until`; the windows of one
   * config abut exactly, with no gap.
   *
   * Paging covers the whole timeline including the live window, so a page never
   * holds more than `limit` entries and the live one — the only entry without a
   * `valid_until` — comes back on exactly one page. Advance `offset` by `limit`;
   * a page past the end is empty, and `total_count` says where that end is
   * without probing for it.
   *
   * `GET /routing/{algorithmId}/history`
   *
   * @param algorithmId - The routing algorithm to read the history of.
   * @param params - Optional paging.
   */
  async history(
    algorithmId: string,
    params: RoutingHistoryParams = {},
  ): Promise<RoutingConfigHistoryResponse> {
    return this.request('GET', `/routing/${encodeURIComponent(algorithmId)}/history`, {
      query: params as Record<string, number | null | undefined>,
    });
  }

  /**
   * A shop's per-connector caps, each with its unit and window and how much of
   * the current window is spent (`used`) or held by payments in flight
   * (`reserved`).
   *
   * `GET /routing/connector-caps/{profileId}`
   */
  async connectorCaps(profileId: string): Promise<RoutingConnectorCaps> {
    return this.request('GET', `/routing/connector-caps/${encodeURIComponent(profileId)}`);
  }

  /**
   * The live `routing_volume` counters behind a shop's active advanced
   * program: one per distinct budget the program reads, each with the window
   * it covers, the figure in the pinned threshold currency, and the rules that
   * read it. Lets a merchant see whether a `routing_volume < 50000` rule is at
   * 120 or at 499 today, and support answer why a payment went to the overflow
   * connector.
   *
   * What routing will use right now, not what the shop turned over: the
   * counters are held in Redis only and a lost Redis restarts the window at
   * zero. Read-only; needs the same permission as reading the rules.
   *
   * `GET /routing/volume-counters/{profileId}`
   */
  async volumeCounters(profileId: string): Promise<RoutingVolumeCounters> {
    return this.request('GET', `/routing/volume-counters/${encodeURIComponent(profileId)}`);
  }

  /**
   * Replace a shop's per-connector caps.
   *
   * Whole-set replacement, not a patch: the list sent becomes the complete set
   * of capped connectors, and an empty list clears them all — which is how
   * acquirer onboarding finishes, the new account ceasing to be a special case.
   * Re-send every other cap with its `unit`, `window` and `currency` as read:
   * an amount cap sent back without its `unit` is refused, because it carries a
   * currency and would otherwise be a payment count.
   *
   * Every account named must belong to this shop; one that does not is refused.
   * So is an amount cap without a `currency`, and a payment cap with one.
   *
   * `PUT /routing/connector-caps/{profileId}`
   */
  async setConnectorCaps(
    profileId: string,
    params: RoutingConnectorCaps,
  ): Promise<RoutingConnectorCaps> {
    return this.request('PUT', `/routing/connector-caps/${encodeURIComponent(profileId)}`, {
      body: params,
    });
  }

  /**
   * List all routing algorithms for the current merchant.
   *
   * @returns The routing dictionary (records + currently active id).
   */
  async list(): Promise<RoutingDictionary> {
    return this.request('GET', '/routing');
  }

  /**
   * Connector names denied at routing for a shop (explicit denies plus
   * whitelist-implied exclusions). Read-only; surfaced in the routing builder.
   *
   * `GET /routing/connector-restrictions/{profileId}`
   */
  async connectorRestrictions(profileId: string): Promise<ProfileDeniedConnectorsResponse> {
    return this.request('GET', `/routing/connector-restrictions/${encodeURIComponent(profileId)}`);
  }

  // --- Advanced operations (Task 3.4) ---

  /** Get active routing config. `GET /routing/active` */
  async getActive(): Promise<LinkedRoutingConfigRetrieveResponse> {
    return this.request('GET', '/routing/active');
  }

  /** Update default routing config. `POST /routing/default` */
  async updateDefault(params: Record<string, unknown>): Promise<RoutableConnectorChoice[]> {
    return this.request('POST', '/routing/default', { body: params });
  }

  /** Retrieve default config for profiles. `GET /routing/default/profile` */
  async getDefaultProfile(): Promise<RoutableConnectorChoice[] | ProfileDefaultRoutingConfig[]> {
    return this.request('GET', '/routing/default/profile');
  }

  /**
   * Retrieve one profile's default config. `GET /routing/default/profile/{profileId}`
   *
   * The narrow counterpart of `getDefaultProfile()`: that one returns every
   * profile of the merchant and needs merchant-level routing read, so a
   * profile-scoped (shop) role could write its own default through
   * `updateDefaultProfile()` but never read it back. This read is gated on
   * profile-level routing read and pinned to the caller's own profile — a shop
   * role asking for a sibling profile is refused.
   */
  async getDefaultForProfile(profileId: string): Promise<ProfileDefaultRoutingConfig> {
    return this.request('GET', `/routing/default/profile/${encodeURIComponent(profileId)}`);
  }

  /** Update default config for a profile. `POST /routing/default/profile/{profileId}` */
  async updateDefaultProfile(
    profileId: string,
    params: Record<string, unknown>,
  ): Promise<ProfileDefaultRoutingConfig> {
    return this.request('POST', `/routing/default/profile/${encodeURIComponent(profileId)}`, {
      body: params,
    });
  }

  /** List routing configs for profile. `GET /routing/list/profile` */
  async listForProfile(): Promise<RoutingDictionary> {
    return this.request('GET', '/routing/list/profile');
  }

  /** Evaluate a routing rule. `POST /routing/rule/evaluate` */
  async evaluateRule(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/routing/rule/evaluate', { body: params });
  }

  /** Migrate routing rules for profile. `POST /routing/rule/migrate` */
  async migrateRule(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/routing/rule/migrate', { body: params });
  }

  /** Evaluate routing for a payment. `POST /routing/evaluate` */
  async evaluate(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/routing/evaluate', { body: params });
  }

  /** Update gateway scores for dynamic routing. `POST /routing/feedback` */
  async feedback(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('POST', '/routing/feedback', { body: params });
  }
}

class RoutingDecisionManager {
  constructor(private readonly request: RequestFn) {}

  /** Upsert decision manager config. `PUT /routing/decision` */
  async upsert(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('PUT', '/routing/decision', { body: params });
  }

  /** Retrieve decision manager config. `GET /routing/decision` */
  async retrieve(): Promise<Record<string, unknown>> {
    return this.request('GET', '/routing/decision');
  }

  /** Delete decision manager config. `DELETE /routing/decision` */
  async delete(): Promise<Record<string, unknown>> {
    return this.request('DELETE', '/routing/decision');
  }

  /** Upsert surcharge decision config. `PUT /routing/decision/surcharge` */
  async upsertSurcharge(params: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request('PUT', '/routing/decision/surcharge', { body: params });
  }

  /** Retrieve surcharge decision config. `GET /routing/decision/surcharge` */
  async retrieveSurcharge(): Promise<Record<string, unknown>> {
    return this.request('GET', '/routing/decision/surcharge');
  }

  /** Delete surcharge decision config. `DELETE /routing/decision/surcharge` */
  async deleteSurcharge(): Promise<Record<string, unknown>> {
    return this.request('DELETE', '/routing/decision/surcharge');
  }
}

/**
 * Merchant-friendly surcharge rules: configure a per-payment-method surcharge
 * (fixed or %) added to the amount the buyer pays — without hand-writing the
 * Euclid DSL. Scope to a shop via `profile_id` (omit for merchant-wide).
 *
 * Distinct from the platform fee (a balance deduction): a surcharge changes what
 * the buyer is charged.
 */
class SurchargeRules {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create or replace the surcharge program for a scope.
   *
   * `PUT /routing/surcharge/rules`
   *
   * @example
   * ```typescript
   * await delopay.routing.surchargeRules.upsert({
   *   surcharges: [
   *     { payment_method: 'crypto', surcharge: { rate: { percent: 1.0 } } },
   *     { payment_method: 'card', surcharge: { fixed: { amount: 35 } } },
   *   ],
   * });
   * ```
   */
  async upsert(params: SurchargeRuleRequest): Promise<SurchargeRuleResponse> {
    return this.request('PUT', '/routing/surcharge/rules', { body: params });
  }

  /**
   * Retrieve the active surcharge program for a scope, or `null` when none is set.
   *
   * `GET /routing/surcharge/rules?profile_id={profileId}`
   *
   * @param profileId - Shop scope. Omit for the merchant-wide rule.
   */
  async retrieve(profileId?: string): Promise<SurchargeRuleResponse | null> {
    return this.request('GET', '/routing/surcharge/rules', {
      query: { profile_id: profileId },
    });
  }

  /**
   * Deactivate the active surcharge program for a scope.
   *
   * `DELETE /routing/surcharge/rules?profile_id={profileId}`
   *
   * @param profileId - Shop scope. Omit for the merchant-wide rule.
   */
  async delete(profileId?: string): Promise<void> {
    return this.request('DELETE', '/routing/surcharge/rules', {
      query: { profile_id: profileId },
    });
  }
}

/**
 * Checkout theme programs: which of a shop's stored appearance variants a buyer
 * is shown.
 *
 * Same engine and same wire format as the advanced routing rules above — a
 * Euclid program whose rules run top-down, first match wins, with
 * `defaultSelection` as the fallback — with the output swapped for a variant
 * name. That is deliberate: the dashboard's routing rule builder can author
 * these without learning a second condition language.
 *
 * **A theme program decides a look and nothing else.** It cannot express which
 * payment methods are offered, what is charged, which provider processes the
 * payment, or whether it succeeds. The allowed dimensions are fixed server-side
 * by the output type — see {@link CheckoutThemeDimension} — so that is a
 * property of the API rather than a convention.
 *
 * Naming a variant the shop has not defined is **not** an error: the checkout
 * falls back to the shop default, exactly as it does for an unknown `?theme=`,
 * because a buyer who cannot pay is worse than a buyer who sees the default
 * look. Such names come back in `warnings` instead, recomputed on every read.
 */
class CheckoutThemeRules {
  constructor(private readonly request: RequestFn) {}

  /**
   * Create or replace the theme program for a scope.
   *
   * `PUT /routing/checkout-theme/rules`
   *
   * Supersedes rather than overwrites: the previous active version is retired
   * and a new one stored, so the record of which look was live when survives.
   * Pass `active: false` to store a revision **without** retiring the live one —
   * that is where a program drafted against a variant you have not built yet
   * belongs.
   *
   * @example A phone in Germany gets the compact look; everyone else the house style.
   * ```typescript
   * await delopay.routing.checkoutThemeRules.upsert({
   *   name: 'Autumn targeting',
   *   profile_id: 'pro_...',
   *   algorithm: {
   *     rules: [
   *       {
   *         name: 'German phones',
   *         connectorSelection: { theme: { variant: 'compact' } },
   *         statements: [
   *           {
   *             condition: [
   *               {
   *                 lhs: 'device_class',
   *                 comparison: 'equal',
   *                 value: { type: 'enum_variant', value: 'phone' },
   *                 metadata: {},
   *               },
   *               {
   *                 lhs: 'browser_language',
   *                 comparison: 'equal',
   *                 value: { type: 'enum_variant', value: 'de' },
   *                 metadata: {},
   *               },
   *             ],
   *           },
   *         ],
   *       },
   *     ],
   *     defaultSelection: { theme: { variant: 'house' } },
   *     metadata: {},
   *   },
   * });
   * ```
   */
  async upsert(params: CheckoutThemeProgramRequest): Promise<CheckoutThemeProgramResponse> {
    return this.request('PUT', '/routing/checkout-theme/rules', { body: params });
  }

  /**
   * Retrieve the active theme program for a scope, or `null` when none is set.
   *
   * `GET /routing/checkout-theme/rules?profile_id={profileId}`
   *
   * @param profileId - Shop scope. Omit for the merchant-wide program. A
   *   shop-scoped caller that omits it gets its own shop's program.
   */
  async retrieve(profileId?: string): Promise<CheckoutThemeProgramResponse | null> {
    return this.request('GET', '/routing/checkout-theme/rules', {
      query: { profile_id: profileId },
    });
  }

  /**
   * Deactivate the active theme program for a scope. Idempotent.
   *
   * `DELETE /routing/checkout-theme/rules?profile_id={profileId}`
   *
   * Deactivation, not deletion — the stored row is what says which look was
   * live when, and that history cannot be reconstructed after the fact. Shops
   * go back to their default appearance immediately.
   *
   * @param profileId - Shop scope. Omit for the merchant-wide program.
   */
  async delete(profileId?: string): Promise<void> {
    return this.request('DELETE', '/routing/checkout-theme/rules', {
      query: { profile_id: profileId },
    });
  }
}

/**
 * Rendered-to-paid conversion, per appearance variant and segment.
 *
 * Answers the one question theme targeting exists for: *does variant B convert
 * better than the house style, on phones, in Germany?*
 *
 * **What a rate here means.** The denominator is backend-observed checkout page
 * opens, deduplicated by device within the hosted checkout's cache window, with
 * automated traffic excluded from both sides. It is narrower than "paints", and
 * that is deliberate - counting one buyer's refresh as a second render would
 * understate conversion. The exact basis travels with every response in
 * `denominator`, and the ways it is not the whole truth travel in `caveats`.
 *
 * **The server decides what may be concluded, not you.** Rate, Wilson interval,
 * per-cell `verdict` and the variant-vs-default `separates` test are computed
 * once, server-side, and shipped as values. Do not recompute them: the first
 * client that rounds differently tells a merchant a difference is real when the
 * server says it is not.
 */
class CheckoutThemeConversion {
  constructor(private readonly request: RequestFn) {}

  /**
   * Rendered-to-paid conversion for a shop over a window.
   *
   * `GET /routing/checkout-theme/conversion`
   *
   * Requires `CheckoutBranding` read at profile scope - the same permission
   * that governs seeing how a checkout looks.
   *
   * @param params - Window (RFC3339, `start` inclusive / `end` exclusive), the
   *   shop, and the dimension to group by.
   *
   * @example Which variant wins on which device, over the last 30 days.
   * ```typescript
   * const report = await delopay.routing.checkoutThemeConversion.retrieve({
   *   profile_id: 'pro_...',
   *   start: '2026-07-21T00:00:00Z',
   *   end: '2026-08-20T00:00:00Z',
   *   segment: 'device',
   * });
   *
   * for (const c of report.comparisons) {
   *   if (!c.separates) continue; // "not shown to differ" - say nothing
   *   console.log(`${c.variant} on ${c.segment}: ${c.higher} converts better`);
   * }
   * ```
   */
  async retrieve(params: CheckoutThemeConversionQuery): Promise<CheckoutThemeConversionResponse> {
    return this.request('GET', '/routing/checkout-theme/conversion', {
      query: {
        profile_id: params.profile_id,
        start: params.start,
        end: params.end,
        segment: params.segment,
      },
    });
  }
}
